import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const monsterActive =
      (typeof window.__monsterCartActive === 'function' && window.__monsterCartActive()) ||
      (typeof window.__monsterCartPending === 'function' && window.__monsterCartPending());
    // Monster Cart is on the page: let it show the cart instead of the theme drawer.
    if (monsterActive && event.action === 'add') {
      event.promise?.then(() => typeof window.openCart === 'function' && window.openCart()).catch(() => {});
    }
    const shouldAutoOpen =
      this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen && !monsterActive;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Open straight away on tap and show a placeholder row, instead of waiting for the
    // add request (~1s) to finish. The placeholder is removed when the real cart renders.
    if (shouldAutoOpen && !sourceModal) {
      this.#showPending(event.target);
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          if (detail?.didError && shouldAutoOpen && !sourceModal) {
            this.#clearPending();
            // Nothing was added to an empty cart: don't leave an empty drawer sitting open.
            if (this.#isCartEmpty()) this.#themeDrawer?.close();
          }
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      })
      .finally(() => this.#clearPending());
  };

  /** @type {number | undefined} */
  #pendingTimeout;

  /**
   * Shows a skeleton "adding" row at the top of the drawer items while the add request is in flight.
   */
  /**
   * @param {EventTarget | null} [source]
   */
  #showPending(source) {
    this.setAttribute('data-adding', '');
    // Reuse the photo the shopper is already looking at (product page gallery or the product
    // card they tapped). It's already in the browser cache, so it appears instantly.
    const scope =
      (source instanceof Element ? source.closest('product-card') : null) || document.querySelector('media-gallery');
    const photo = scope?.querySelector('.product-media-container img, img');
    const photoSrc = photo instanceof HTMLImageElement ? photo.currentSrc || photo.src : '';
    const items = this.querySelector('.cart-drawer__items');
    if (items && !items.querySelector(':scope > .cart-drawer__pending')) {
      const row = document.createElement('div');
      row.className = 'cart-drawer__pending';
      row.setAttribute('role', 'status');
      row.innerHTML =
        '<span class="cart-drawer__pending-image"></span>' +
        '<span class="cart-drawer__pending-lines"><span></span><span></span></span>' +
        '<span class="visually-hidden">Adding to cart</span>';
      if (photoSrc) {
        const img = document.createElement('img');
        img.className = 'cart-drawer__pending-image cart-drawer__pending-image--photo';
        img.alt = '';
        img.src = photoSrc;
        row.firstElementChild?.replaceWith(img);
      }
      items.prepend(row);
    }
    // Safety net: never leave the placeholder up if something goes wrong.
    clearTimeout(this.#pendingTimeout);
    this.#pendingTimeout = window.setTimeout(() => this.#clearPending(), 8000);
  }

  #clearPending() {
    clearTimeout(this.#pendingTimeout);
    this.removeAttribute('data-adding');
    this.querySelectorAll('.cart-drawer__pending').forEach((el) => el.remove());
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
